import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import * as XLSX from 'xlsx';
import { useProductsStore } from 'src/store/products/products.store';
import { usePartnersStore } from 'src/store/partners/partners.store';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { convertFile } from 'src/utils/convert-file.utils';
import { useLpEventsStore } from 'src/store/benefits/lp-events.store';
import { useMyDataStore } from 'src/store/me/me.store';
import { paths } from 'src/routes/paths';

const HIGH_OPTIONS = ['without', 'home_highlight', 'product_highlight', 'benefits_highlight'];
const HIGH_LABEL = [
  'Sem Destaque',
  'Destaque Home',
  'Destaque Produtos',
  'Destaque Benefícios Exclusivos',
];

const optionsHighlight = [
  {
    value: 'without',
    label: 'Sem Destaque',
  },
  {
    value: 'home_highlight',
    label: 'Destaque Home',
  },
  {
    value: 'product_highlight',
    label: 'Destaque Produtos',
  },
  {
    value: 'benefits_highlight',
    label: 'Destaque Benefícios Exclusivos',
  },
  {
    value: 'lp_events_home_highlights',
    label: 'Destaque Home Viva Visitante',
  },
];

const STATUS_LABEL = ['Ativo', 'Inativo'];
const STATUS_OPTIONS = ['active', 'inactive'];

const PRODUCT_TYPE_LABEL = ['Item', 'Código/Link'];
const PRODUCT_TYPE_OPTIONS = ['simple', 'code'];

const PRODUCT_WPP_TYPE_LABEL = ['Item', 'Código Único'];
const PRODUCT_WPP_TYPE_OPTIONS = ['simple', 'uniqueCode'];

const PRODUCT_CODE_LABEL = ['Campo para copiar', 'Botão de redirecionamento'];
const PRODUCT_CODE_OPTIONS = ['code', 'link'];

const LOCAL_TYPE_LABEL = ['Empresa', 'Local'];
const LOCAL_TYPE_OPTIONS = ['company', 'local'];

const PRIVATE_LABEL = ['Privado', 'Público'];
const PRIVATE_OPTIONS = [true, false];

const DONT_LABEL = ['Importados', 'Não importados'];
const DONT_OPTIONS = [true, false];

const BLOCK_REDEEM_OPTIONS = ['all', 'self'];
const BLOCK_REDEEM_LABEL = ['Sim', 'Não'];

const BOOLEAN_LABELS = ['Sim', 'Não'];
const BOOLEAN_OPTIONS = [true, false];

const TIERS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'silver', label: 'Prata' },
  { value: 'gold', label: 'Ouro' },
  { value: 'diamond', label: 'Diamante' },
];

const EXHIBITION_ENV_OPTIONS = [
  { value: 'viva', label: 'VIVA CENTER NORTE' },
  { value: 'lp_events', label: 'Viva Visitante' },
  { value: 'whatsapp', label: 'WHATSAPP' },
];

const TEST_OPTIONS = [true, false];
const TEST_LABEL = ['Sim', 'Não'];

const BONUS_PRODUCT_OPTIONS = [true, false];
const BONUS_PRODUCT_LABEL = ['Sim', 'Não'];
const NEWS_OPTIONS = [true, false];
const NEWS_LABEL = ['Sim', 'Não'];

const BIOME_OPTIONS = [true, false];
const BIOME_LABEL = ['Sim', 'Não'];

// type ISelectedOption = 'store' | 'company' | 'location';

export const useLPProductDetails = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isEdit = pathname.includes('edit');
  const [searchParams] = useSearchParams();
  const copyId = searchParams.get('copy');
  const title = isEdit ? 'Editar Produto' : 'Criar Produto';
  const { id } = useParams();
  const [isDetailsLoading, setIsDetailsLoading] = useState(isEdit && Boolean(id));
  const [defaultOption, setDefaultOption] = useState<{
    value: string;
    label: string;
    type: string;
  } | null>(null);
  const [upload] = usePartnersStore((state) => [state.getUrlFile]);
  const [
    stores,
    categories,
    themes,
    options,
    findLocations,
    findCategories,
    createLPProduct,
    findById,
    updateLPProduct,
    findThemes,
    changeLPProductStatus,
  ] = useProductsStore((state) => [
    state.stores,
    state.categories,
    state.themes,
    state.locations,
    state.getLocations,
    state.getCategories,
    state.createLPProduct,
    state.findById,
    state.updateLPProduct,
    state.getThemes,
    state.changeLPProductStatus,
  ]);

  const [businessUnits, lpEventsByBusinessUnit, getBusinessUnits, getLpEventsByBusinessUnit] =
    useLpEventsStore((state) => [
      state.businessUnits,
      state.lpEventsByBusinessUnit,
      state.getBusinessUnits,
      state.getLpEventsByBusinessUnit,
    ]);

  const user = useMyDataStore((state) => state.me);

  const { enqueueSnackbar } = useSnackbar();
  const [codes, setCodes] = useState<any[]>([]);
  const [cpfs, setCpfs] = useState<any[]>([]);
  const [redeemsToSet, setRedeemsToSet] = useState<{ value: string; label: string }[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [fileNameCpfs, setFileNameCpfs] = useState<string>('');
  const [openCpfs, setOpenCpfs] = useState<boolean>(false);
  const [currentRedeem, setCurrentRedeem] = useState<any>(null);
  const [openRedeemModal, setOpenRedeemModal] = useState<boolean>(false);
  const [renderedLocOptions, setRenderedLocOptions] = useState<any[]>([]);
  const [showToLocations, setShowToLocations] = useState<any[]>([]);
  const [filteredHighlights, setFilteredHighlights] = useState<any[]>([]);

  const [showVivaForm, setShowVivaForm] = useState<boolean>(false);
  const [showLpEventsForm, setShowLpEventsForm] = useState<boolean>(false);
  const [showWhatsAppForm, setShowWhatsAppForm] = useState<boolean>(false);

  const isLpExhibitor = user?.roles?.some((role: string) => role === 'lp_exhibitor');

  const [codesEdited, setCodesEdited] = useState(false);
  const defaultValues = useMemo(
    () => ({
      image: '',
      name: '',
      description: '',
      category: '',
      theme: '',
      highlightCategory: [],
      status: 'inactive',
      price: 0,
      show: [],
      localType: 'local',
      startDate: new Date(),
      endDate: new Date(),
      rewardType: 'simple',
      quantityCode: 0,
      totalQuantity: 0,
      expirationDays: 0,
      exhibitionEnv: EXHIBITION_ENV_OPTIONS[1].value,
      store: '',
      company: '',
      local: '',
      location: '',
      priority: 0,
      rules: '',
      tiers: [],
      details: !isEdit ? [{ value: '' }] : [],
      private: false,
      dontShowToImported: false,
      blocking: 'self',
      userPurchaseLimit: 1,
      isGift: false,
      codeShowType: 'code',
      test: false,
      bonusProduct: false,
      redeemCodeQuantity: 1,
      news: false,
      tag: '',
      exclusiveToBiome: false,
      businessUnitId: user?.lpEventsData?.businessUnitId || '',
      lpEvent: user?.lpEventsData?.eventId || '',
      hasQuantity: false,
      uniqueCode: '',
      onlyToRedeemedPoints: false,
      editComment: '',
    }),
    [isEdit]
  );

  const productSchema = Yup.object().shape({
    name: Yup.string().required('Nome é obrigatório'),
    image: Yup.mixed<string | File>()
      .required('Imagem é obrigatória')
      .test('image-required', 'Imagem é obrigatória', (value) =>
        typeof value === 'string'
          ? Boolean(value.trim())
          : typeof File !== 'undefined' && value instanceof File
      ),
    description: Yup.string().when('exhibitionEnv', {
      is: (val: string) => val !== 'whatsapp',
      then: (schema) => schema.required('Descrição é obrigatória'),
      otherwise: (schema) => schema.nullable(),
    }),
    category: Yup.string().when('exhibitionEnv', {
      is: (val: string) => val !== 'whatsapp',
      then: (schema) => schema.required('Categoria é obrigatória'),
      otherwise: (schema) => schema.nullable(),
    }),
    theme: Yup.string(),
    highlightCategory: Yup.array(),
    status: Yup.string().required('Status é obrigatório'),
    price: Yup.number().required('Preço é obrigatório'),
    show: Yup.array().when('exhibitionEnv', {
      is: 'viva',
      then: (schema) => schema.min(1, 'Selecione pelo menos uma opção'),
      otherwise: (schema) => schema,
    }),
    startDate: Yup.date().required('Data de início é obrigatória'),
    endDate: Yup.date()
      .required('Data de término é obrigatória')
      .test(
        'is-minus-than-start',
        'Data de término deve ser maior que a data de início',
        (v, context) => {
          const { startDate } = context.parent;
          const start = new Date(startDate);
          const end = new Date(v);
          return end.getTime() >= start.getTime();
        }
      ),
    rewardType: Yup.string().required('Tipo de produto é obrigatório'),
    quantityCode: Yup.number().required('Quantidade de códigos é obrigatória'),
    totalQuantity: Yup.number().when('rewardType', {
      is: 'simple',
      then: (schema) =>
        schema
          .required('Quantidade total é obrigatória')
          .min(1, 'A quantidade deve ser maior que zero'),
      otherwise: (schema) => schema.nullable(),
    }),
    expirationDays: Yup.number()
      .required('Campo obrigatório')
      .positive('O valor deve ser maior que zero')
      .integer('O valor deve ser um número inteiro'),
    store: Yup.mixed().nullable(),
    company: Yup.mixed().when('localType', {
      is: 'company',
      then: (schema) => schema.required('Empresa é obrigatória'),
      otherwise: (schema) => schema.nullable(),
    }),
    local: Yup.mixed()
      .required('Localização é obrigatória')
      .test('selected-location', 'Localização é obrigatória', (value) =>
        Boolean(
          value &&
          typeof value === 'object' &&
          'value' in value &&
          typeof value.value === 'string' &&
          value.value.trim()
        )
      ),
    exhibitionEnv: Yup.string().required('Ambiente de exibição é obrigatório'),
    location: Yup.string(),
    rules: Yup.string().required('Regras são obrigatórias'),
    details: Yup.array().when('exhibitionEnv', {
      is: 'viva',
      then: (schema) =>
        schema.min(1, 'Pelo menos um detalhe é obrigatório').of(
          Yup.object().shape({
            value: Yup.string()
              .required('O detalhe é obrigatório')
              .min(3, 'O detalhe deve ter pelo menos 3 caracteres'),
          })
        ),
      otherwise: (schema) => schema,
    }),
    localType: Yup.string(),
    tiers: Yup.array().test(
      'is-not-empty',
      'Selecione pelo menos uma opção',
      (v) => v && v.length > 0
    ),
    priority: Yup.number(),
    private: Yup.boolean(),
    dontShowToImported: Yup.boolean(),
    blocking: Yup.string().required(),
    userPurchaseLimit: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(1, 'O número deve ser maior ou igual a 1!'),
    isGift: Yup.boolean(),
    codeShowType: Yup.string(),
    test: Yup.boolean(),
    bonusProduct: Yup.boolean(),
    redeemCodeQuantity: Yup.number()
      .typeError('Deve ser um número!')
      .integer('Deve ser um número inteiro!')
      .min(1, 'O número deve ser maior ou igual a 1!'),
    news: Yup.boolean(),
    tag: Yup.string().nullable(),
    exclusiveToBiome: Yup.boolean(),
    businessUnitId: Yup.string().when('exhibitionEnv', {
      is: 'lp_events',
      then: (schema) => schema.required('Unidade de Negócio é obrigatória'),
      otherwise: (schema) => schema.nullable(),
    }),
    lpEvent: Yup.string().when('exhibitionEnv', {
      is: 'lp_events',
      then: (schema) => schema.required('Unidade de Negócio é obrigatória'),
      otherwise: (schema) => schema.nullable(),
    }),
    hasQuantity: Yup.boolean(),
    uniqueCode: Yup.string().when('rewardType', {
      is: 'uniqueCode',
      then: (schema) => schema.required('Código único é obrigatório'),
      otherwise: (schema) => schema.nullable(),
    }),
    onlyToRedeemedPoints: Yup.boolean(),
    editComment: Yup.string().when([], {
      is: () => isEdit,
      then: (schema) => schema.required('Motivo da edição é obrigatório'),
      otherwise: (schema) => schema.nullable(),
    }),
  });

  const methods = useForm({
    defaultValues,
    // @ts-ignore
    resolver: yupResolver(productSchema),
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    control,
    watch,
  } = methods;

  const {
    fields: fieldsDetails,
    append: appendDetails,
    remove: removeDetails,
  } = useFieldArray({
    control,
    // @ts-ignore
    name: 'details',
  });

  const reward = watch('rewardType');
  const end = watch('endDate');
  const start = watch('startDate');
  const show = watch('show');
  const i = watch('image');
  const highlight = watch('highlightCategory');
  const localType = watch('localType');
  const tier = watch('tiers');
  const isPrivate = watch('private');
  const exhibitionEnv = watch('exhibitionEnv');
  const redeemCodeQuantity = watch('redeemCodeQuantity');
  const isAtNews = JSON.parse(String(watch('news') || 'false'));
  const businessUnitId = watch('businessUnitId');
  const hasQuantity = JSON.parse(String(watch('hasQuantity') || 'false'));

  useEffect(() => {
    if (!isAtNews) {
      setValue('tag', '');
    }
  }, [isAtNews]);

  useEffect(() => {
    const fetchLpEvents = async () => {
      if (businessUnitId) {
        await getLpEventsByBusinessUnit(businessUnitId);
      }
    };
    fetchLpEvents();
  }, [businessUnitId]);

  const onSubmit = handleSubmit(async (data: any) => {
    if (reward === 'code' && codes.length === 0) {
      enqueueSnackbar('Adicione códigos para continuar', { variant: 'error' });
      return;
    }

    if (
      (!isEdit && reward === 'code' && codes.length % data.redeemCodeQuantity! !== 0) ||
      (isEdit && codesEdited && codes.length % data.redeemCodeQuantity! !== 0)
    ) {
      enqueueSnackbar(
        'Quantidade de códigos/links deve ser múltiplo da quantidade de códigos/links por resgate',
        { variant: 'error' }
      );
      return;
    }

    if (data.localType === 'local' && !data.local) {
      enqueueSnackbar('Local é obrigatório', { variant: 'error' });
      return;
    }

    if (data.localType === 'company' && !data.company) {
      enqueueSnackbar('Empresa é obrigatória', { variant: 'error' });
      return;
    }

    let img = '';
    // @ts-ignore

    if (i) {
      const [image, format] = await convertFile(i);

      if (format === 'error') {
        enqueueSnackbar('Ocorreu um Erro', { variant: 'error' });
        return;
      }
      if (format === 'base64') {
        const uploaded = await upload(image);
        if (uploaded === 'error') {
          enqueueSnackbar('Erro ao fazer upload da imagem', { variant: 'error' });
          return;
        }
        img = uploaded;
      }
      if (format === 'url') {
        img = image;
      }
    }

    let entityId = null;
    let entityName = null;
    if (localType === 'company') {
      // @ts-ignore
      const foundByName = companies.find((c) => c.name === data?.company?.label);
      // @ts-ignore
      entityName = data.company?.label || data?.company;
      // @ts-ignore
      entityId = data?.company?.value || foundByName?._id;
    } else if (localType === 'local') {
      // @ts-ignore
      entityId = data.local?.value;
      // @ts-ignore
      entityName = data.local?.label;
    }

    let newCodes;
    if (codesEdited) {
      newCodes = codes || [];
    }

    const dataToSend = {
      _id: id,
      name: data.name,
      description: data.description,
      category: data.category,
      theme: data.theme || null,
      highlightCategory: ['without'],
      // @ts-ignore
      store: null,
      local: '',
      banner: img || null,
      status: data.status,
      price: data.price,
      // @ts-ignore
      rules: data.rules,
      toShow: data.show,
      startDate: data.startDate,
      endDate: data.endDate,
      rewardType: data.rewardType,
      totalQuantity: data.totalQuantity,
      expiration: {
        expirationType: 'days',
        expirationDays: data.expirationDays,
      },
      codes: newCodes || [],
      cpfs: cpfs.map((c) => c?.[0]) || [],
      partner: currentRedeem?.partnerId?._id,
      partnerName: currentRedeem?.partnerId?.name,
      redeem: currentRedeem?._id,
      redeemName: currentRedeem?.label || currentRedeem?.details?.name || currentRedeem?.name,
      currentRedeemData: currentRedeem || {},
      visibleForTiers:
        Array.isArray(data.tiers) && data.tiers.length === 1 && data.tiers[0] === 'all'
          ? []
          : data.tiers,
      priority: 0,
      // @ts-ignore
      details: data.details.map((d: any) => d.value),
      entity: {
        type: 'local',
        entityId,
        entityName,
      },
      exhibitionEnv: data.exhibitionEnv,
      private: data.private,
      dontShowToImported: data.dontShowToImported,
      blocking: {
        type: data.blocking,
      },
      userPurchaseLimit: {
        limit: data.userPurchaseLimit,
      },
      isGift: data.isGift,
      codeShowType: data.codeShowType,
      test: data.test,
      bonusProduct: data.bonusProduct,
      redeemCodeQuantity: reward === 'code' ? data.redeemCodeQuantity : 1,
      news: data.news,
      tag: data.tag,
      exclusiveToBiome: data.exclusiveToBiome,
      lpEventsData: {
        businessUnitId: data.businessUnitId || null,
        eventId: data.lpEvent || null,
        editComment: data.editComment || null,
        creatorId: user?._id,
        creatorName: user?.name,
        creatorType: (() => {
          if (user?.roles?.includes('lp_shopkeeper')) {
            return 'shopkeeper';
          }
          if (user?.roles?.includes('lp_exhibitor')) {
            return 'exhibitor';
          }
          if (user?.roles?.includes('lp_contractor')) {
            return 'contractor';
          }
          return 'admin';
        })(),
      },
      hasQuantity: data.hasQuantity || false,
      uniqueCode: data.uniqueCode || '',
      onlyToRedeemedPoints: data.onlyToRedeemedPoints || false,
    };

    if (!isEdit) {
      delete dataToSend._id;
      const created = await createLPProduct(dataToSend);
      if (!created) {
        enqueueSnackbar('Erro ao criar produto', { variant: 'error' });
      } else {
        enqueueSnackbar('Produto criado com sucesso', { variant: 'success' });
        navigate(paths.dashboard.lpBenefitsList);
      }
    } else {
      const updated = await updateLPProduct(dataToSend);
      if (!updated) {
        enqueueSnackbar('Erro ao atualizar produto', { variant: 'error' });
      } else {
        enqueueSnackbar('Produto atualizado com sucesso', { variant: 'success' });
        navigate(paths.dashboard.lpBenefitsList);
      }
    }
  });

  const [openComment, setOpenComment] = useState(false);
  const [openRefuse, setOpenRefuse] = useState(false);
  const [openApprove, setOpenApprove] = useState(false);

  const onCloseComment = () => {
    setOpenComment(false);
  };

  const onCloseApprove = () => {
    setOpenApprove(false);
  };

  const handleApprove = async () => {
    setOpenApprove(true);
  };

  const handleRefuse = async () => {
    setOpenRefuse(true);
  };

  const onCloseRefuse = () => {
    setOpenRefuse(false);
  };

  const handleActivateAndInactivate = async () => {
    try {
      if (id) {
        await changeLPProductStatus(id);
        navigate(paths.dashboard.lpBenefitsList);
        enqueueSnackbar('Status alterado com sucesso!', { variant: 'success' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onCloseRedeemModal = () => {
    setOpenRedeemModal(false);
  };

  const handleDrop = useCallback(
    (acceptedFiles: any) => {
      const fileFull = acceptedFiles[0];
      if (fileFull) {
        const allowedImageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
        if (allowedImageTypes.includes(fileFull.type)) {
          const reader = new FileReader();

          reader.onload = () => {
            const img = new Image();
            img.src = reader.result as string;

            img.onload = () => {
              const { width, height } = img;

              if (width > 800 || height > 800) {
                enqueueSnackbar({
                  message: 'Imagem deve ter no máximo 800x800 pixels',
                  variant: 'warning',
                });
                return;
              }

              setValue(
                'image',
                Object.assign(fileFull, {
                  preview: reader.result,
                })
              );
            };
          };

          reader.readAsDataURL(fileFull);
        } else {
          enqueueSnackbar('Erro ao adicionar imagem: formato inválido', { variant: 'error' });
        }
      }
    },
    [setValue]
  );
  const onClose = () => {
    setOpen(false);
  };

  const onCloseCpfs = () => {
    setOpenCpfs(false);
  };

  const handleUpload = (e: any) => {
    setCodesEdited(true);
    e.preventDefault();
    const { files } = e.target;
    // eslint-disable-next-line
    var f = files[0];

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = event.target?.result;
      const readedData = XLSX.read(data, { type: 'binary' });
      const wsname = readedData.SheetNames[0];
      const ws = readedData.Sheets[wsname];
      const dataParse: any = XLSX.utils.sheet_to_json(ws, { header: 1 });

      if (dataParse?.[0]?.[0] !== 'Códigos abaixo') {
        enqueueSnackbar('Modelo de planilha inválido!', { variant: 'error' });
        return;
      }
      let updatedDataParse = dataParse?.slice(1);
      updatedDataParse = updatedDataParse.filter((c: any) => c?.[0]).map((c: any) => c[0]) || [];
      setFileName(f.name);
      setCodes(updatedDataParse);
      enqueueSnackbar('Códigos adicionados com Sucesso!');
    };
    reader.readAsBinaryString(f);
  };

  const handleUploadCpfs = (e: any) => {
    e.preventDefault();
    const { files } = e.target;
    // eslint-disable-next-line
    var f = files[0];

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = event.target?.result;
      const readedData = XLSX.read(data, { type: 'binary' });
      const wsname = readedData.SheetNames[0];
      const ws = readedData.Sheets[wsname];
      const dataParse: any = XLSX.utils.sheet_to_json(ws, { header: 1 });

      if (dataParse?.[0]?.[0] !== 'Cpfs abaixo') {
        enqueueSnackbar('Modelo de planilha inválido!', { variant: 'error' });
        return;
      }
      const updatedDataParse = dataParse?.slice(1);
      setFileNameCpfs(f.name);
      setCpfs(updatedDataParse);
      enqueueSnackbar('Códigos adicionados com Sucesso!');
    };
    reader.readAsBinaryString(f);
  };

  const isValidEndDate = useMemo(() => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return endDate.getTime() >= startDate.getTime();
  }, [end, start]);

  useEffect(() => {
    const func = async () => {
      await Promise.all([findCategories(), findThemes(), findLocations(), getBusinessUnits()]);
    };
    func();
  }, []);

  useEffect(() => {
    // @ts-ignore
    if (Array.isArray(show) && show.includes('all') && show.length > 1) {
      // @ts-ignore
      setValue('show', ['all']);
    }
  }, [show]);

  const [approvalStatus, setApprovalStatus] = useState<string>('');
  const [productStatus, setProductStatus] = useState<string>('');
  const [productDetails, setProductDetails] = useState();

  const findProduct = async (hasCodes: boolean) => {
    const prodId = copyId || id;
    const p = await findById(prodId!, setValue);
    const found = p?.product;
    const codesArray = p?.codes;
    const cpfsArray = p?.product?.cpfs;
    setValue('image', found?.banner);
    setValue('name', found?.name);
    setValue('location', found?.local);
    setValue('category', found?.category);
    setValue('theme', found?.theme || '');
    setValue('priority', found?.priority);
    setValue(
      'highlightCategory',
      Array.isArray(found?.highlightCategory)
        ? found?.highlightCategory
        : [found?.highlightCategory]
    );
    setValue('blocking', found?.blocking.type);
    setValue('status', found?.status);
    setValue('price', found?.price);
    setValue('show', found?.toShow);
    setValue('startDate', new Date(found?.startDate));
    setValue('endDate', new Date(found?.endDate));
    setValue('rewardType', found?.rewardType || 'simple');
    setValue('totalQuantity', found?.totalQuantity);
    setValue('localType', found?.entity?.type);
    setValue('tiers', found?.visibleForTiers?.length === 0 ? ['all'] : found?.visibleForTiers);
    setValue('expirationDays', found?.expiration?.expirationDays);
    setValue('private', found?.private);
    setValue('dontShowToImported', found?.dontShowToImported);
    setValue('exhibitionEnv', found?.exhibitionEnv || 'viva');
    setValue('codeShowType', found?.codeShowType);
    setValue(
      'userPurchaseLimit',
      found?.userPurchaseLimit?.limit ? found?.userPurchaseLimit?.limit : 1
    );
    setValue('test', found?.test);
    setValue('bonusProduct', found?.bonusProduct || false);
    setValue('isGift', found?.isGift || false);
    setValue('news', found?.news);
    setValue('tag', found?.tag);
    setValue('exclusiveToBiome', found?.exclusiveToBiome);
    if (codesArray && codesArray.length > 0 && hasCodes) {
      setCodes(codesArray.map((c: string) => [c]));
    } else {
      setCodes([]);
    }
    if (cpfsArray && cpfsArray.length > 0) {
      setCpfs(cpfsArray.map((c: string) => [c]));
    }
    setValue('rules', found?.rules);
    setValue('details', found?.details.map((d: string) => ({ value: d })) || []);

    setValue('description', found?.description);
    setValue('redeemCodeQuantity', found?.redeemCodeQuantity);
    setValue('businessUnitId', found?.lpEventsData?.businessUnitId || '');
    setValue('lpEvent', found?.lpEventsData?.eventId || '');
    setValue('hasQuantity', found?.hasQuantity || false);
    setValue('uniqueCode', found?.uniqueCode || '');
    setValue('onlyToRedeemedPoints', found?.onlyToRedeemedPoints || false);
    if (found?.currentRedeemData) {
      setCurrentRedeem(found?.currentRedeemData);
    }
    setDefaultOption({
      value: found?.entity?.entityId,
      label: found?.entity?.entityName,
      type: found?.entity?.type,
    });
    setApprovalStatus(p?.product?.lpEventsData?.approvalStatus || '');
    setProductStatus(p?.product?.status || '');
    setProductDetails(found);
  };

  useEffect(() => {
    let active = true;
    const loadProduct = async () => {
      setIsDetailsLoading(true);
      try {
        await findProduct(true);
      } catch (error) {
        console.error(error);
        enqueueSnackbar('Erro ao carregar benefício', { variant: 'error' });
      } finally {
        if (active) setIsDetailsLoading(false);
      }
    };
    if (isEdit && id) {
      loadProduct();
    } else {
      setIsDetailsLoading(false);
    }
    return () => {
      active = false;
    };
  }, [isEdit, id]);

  useEffect(() => {
    // @ts-ignore
    if (highlight.includes('without') && highlight.length > 1) {
      // @ts-ignore
      setValue('highlightCategory', ['without']);
    }
  }, [highlight]);

  useEffect(() => {
    if (localType === defaultOption?.type) {
      // @ts-ignore
      setValue(localType, {
        value: defaultOption?.value,
        label: defaultOption?.label,
      });
    } else if (localType === 'company') {
      setValue('local', '');
      setValue('company', '');
    } else {
      setValue('local', '');
      setValue('company', '');
    }
  }, [localType, defaultOption]);

  useEffect(() => {
    if (Array.isArray(tier) && tier.includes('all') && tier.length > 1) {
      setValue('tiers', ['all']);
    }
  }, [tier]);

  useEffect(() => {
    if (exhibitionEnv === 'lp_events') {
      setShowVivaForm(false);
      setShowLpEventsForm(true);
      setShowWhatsAppForm(false);

      // Whats App fields
      setValue('hasQuantity', false);
      setValue('uniqueCode', '');

      // Viva fields
      setValue('price', 0);
      if (options && options.length > 0 && options[0]) {
        setValue('show', [options[0].value]);
      }
      setValue('tiers', ['all']);
      setValue('redeemCodeQuantity', 1);
      setValue('isGift', false);
      setValue('blocking', 'self');
      setValue('private', false);
      setValue('dontShowToImported', false);
      setValue('test', false);
      setValue('bonusProduct', false);
      setValue('news', false);
      setValue('tag', '');
      setCurrentRedeem(null);

      setFilteredHighlights(
        optionsHighlight.filter(
          (op: { value: string; label: string }) =>
            op.value === 'lp_events_home_highlights' || op.value === 'without'
        )
      );
    }
    setRenderedLocOptions(options || []);
  }, [exhibitionEnv]);

  useEffect(() => {
    setRenderedLocOptions(options || []);
    const allowedLabels = [
      'Shopping Center Norte',
      'Lar Center',
      'Shopping Center Norte, Lar Center',
    ];
    setShowToLocations(
      (options || []).filter((op: { label: string }) => allowedLabels.includes(op.label))
    );
  }, [options]);

  useEffect(() => {
    if (copyId) findProduct(false);
  }, [copyId]);

  useEffect(() => {
    if (isLpExhibitor) {
      setValue('local', { value: '6810c6cca9f4a61b35dd71c3', label: 'Expo Center Norte' });
    }
  }, [isLpExhibitor]);

  return {
    cpfs,
    open,
    codes,
    title,
    isEdit,
    themes,
    reward,
    stores,
    methods,
    control,
    openCpfs,
    isAtNews,
    fileName,
    localType,
    isPrivate,
    categories,
    hasQuantity,
    codesEdited,
    isSubmitting,
    isDetailsLoading,
    fileNameCpfs,
    redeemsToSet,
    showVivaForm,
    businessUnits,
    exhibitionEnv,
    fieldsDetails,
    currentRedeem,
    isValidEndDate,
    openRedeemModal,
    showToLocations,
    showLpEventsForm,
    showWhatsAppForm,
    renderedLocOptions,
    filteredHighlights,
    redeemCodeQuantity,
    lpEventsByBusinessUnit,
    HIGH_LABEL,
    HIGH_OPTIONS,
    DONT_LABEL,
    DONT_OPTIONS,
    STATUS_LABEL,
    STATUS_OPTIONS,
    PRIVATE_LABEL,
    PRIVATE_OPTIONS,
    TIERS_OPTIONS,
    BOOLEAN_LABELS,
    BOOLEAN_OPTIONS,
    LOCAL_TYPE_LABEL,
    LOCAL_TYPE_OPTIONS,
    PRODUCT_TYPE_LABEL,
    PRODUCT_TYPE_OPTIONS,
    PRODUCT_CODE_LABEL,
    PRODUCT_CODE_OPTIONS,
    BLOCK_REDEEM_LABEL,
    BLOCK_REDEEM_OPTIONS,
    PRODUCT_WPP_TYPE_LABEL,
    PRODUCT_WPP_TYPE_OPTIONS,
    EXHIBITION_ENV_OPTIONS,
    TEST_OPTIONS,
    TEST_LABEL,
    BONUS_PRODUCT_OPTIONS,
    BONUS_PRODUCT_LABEL,
    NEWS_LABEL,
    NEWS_OPTIONS,
    BIOME_OPTIONS,
    BIOME_LABEL,
    openComment,
    openRefuse,
    openApprove,
    id,
    approvalStatus,
    productStatus,
    productDetails,
    setOpen,
    onClose,
    setValue,
    onSubmit,
    handleDrop,
    setOpenCpfs,
    onCloseCpfs,
    handleUpload,
    appendDetails,
    removeDetails,
    enqueueSnackbar,
    setRedeemsToSet,
    handleUploadCpfs,
    setCurrentRedeem,
    setOpenRedeemModal,
    onCloseRedeemModal,
    onCloseComment,
    setOpenComment,
    handleApprove,
    handleRefuse,
    onCloseRefuse,
    onCloseApprove,
    handleActivateAndInactivate,
    watch,
  };
};
